'use client';
import React, { useState, useEffect, useRef } from 'react';
import AppNav from '@/components/AppNav';
import { supabase } from '@/lib/supabase/client';

interface Document {
  id: string;
  document_type: string;
  file_name: string;
  status: string;
  passport_match_score: number | null;
  mismatch_fields: Record<string, string> | null;
  created_at: string;
  file_url?: string;
}

const documentTypeLabels: Record<string, string> = {
  pan: 'PAN Card',
  aadhaar: 'Aadhaar Card',
  address_proof: 'Address Proof',
  noc: 'NOC',
  photo: 'Photograph',
  bank_statement: 'Bank Statement',
  incorporation_doc: 'Incorporation Document',
  trademark_doc: 'Trademark Document',
  authorization: 'Authorization Letter',
};

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  verified: { label: 'Verified', color: 'text-green-700', bg: 'bg-green-50' },
  pending: { label: 'Pending upload', color: 'text-slate-600', bg: 'bg-slate-100' },
  processing: { label: 'Processing', color: 'text-indigo-700', bg: 'bg-indigo-50' },
  rejected: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-50' },
  mismatch: { label: 'Mismatch', color: 'text-amber-800', bg: 'bg-amber-50' },
};

export default function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploadDocId, setPendingUploadDocId] = useState<string | null>(null);

  useEffect(() => {
    async function loadDocuments() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        // Get the user's business first (multi-tenant: scope to user's business)
        const { data: business, error: bizErr } = await supabase
          .from('businesses')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
          .single();

        if (bizErr && bizErr.code !== 'PGRST116') throw bizErr;

        if (business) {
          setBusinessId(business.id);
          const { data, error: docsErr } = await supabase
            .from('documents')
            .select('*')
            .eq('business_id', business.id)
            .order('created_at', { ascending: false });

          if (docsErr) throw docsErr;
          setDocuments(data || []);
        }
      } catch (err) {
        console.error('Documents load error:', err);
        setError('Failed to load documents. Please refresh.');
      } finally {
        setLoading(false);
      }
    }
    loadDocuments();
  }, []);

  const handleUploadClick = (docId: string) => {
    setPendingUploadDocId(docId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUploadDocId || !businessId) return;

    setUploading(pendingUploadDocId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload to Supabase Storage (scoped to business)
      const filePath = `${businessId}/${pendingUploadDocId}/${file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('documents')
        .upload(filePath, file, { upsert: true });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      // Update document record
      const { error: updateErr } = await supabase
        .from('documents')
        .update({
          status: 'processing',
          file_name: file.name,
          file_url: urlData.publicUrl,
        })
        .eq('id', pendingUploadDocId)
        .eq('business_id', businessId); // RLS enforcement

      if (updateErr) throw updateErr;

      setDocuments(prev => prev.map(d =>
        d.id === pendingUploadDocId
          ? { ...d, status: 'processing', file_name: file.name, file_url: urlData.publicUrl }
          : d
      ));

      // Simulate AI processing (in production this would be a background job)
      setTimeout(async () => {
        await supabase
          .from('documents')
          .update({ status: 'verified', passport_match_score: 95 })
          .eq('id', pendingUploadDocId)
          .eq('business_id', businessId);

        setDocuments(prev => prev.map(d =>
          d.id === pendingUploadDocId
            ? { ...d, status: 'verified', passport_match_score: 95 }
            : d
        ));
      }, 3000);

    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(null);
      setPendingUploadDocId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const verifiedCount = documents.filter(d => d.status === 'verified').length;
  const pendingCount = documents.filter(d => d.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/documents" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-surface-3 rounded-xl w-48" />
              <div className="h-20 bg-white rounded-xl border border-border" />
              {[1,2,3,4].map(i => <div key={i} className="h-20 bg-white rounded-xl border border-border" />)}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/documents" />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        aria-hidden="true"
        onChange={handleFileChange}
      />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="flex items-start justify-between mb-8 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-ink mb-1">Document Center</h1>
              <p className="text-[14px] text-ink-muted">Upload and verify documents. AI extracts and validates information automatically.</p>
            </div>
            {documents.length > 0 && (
              <div className="text-right flex-shrink-0">
                <div className="text-2xl font-bold text-ink">{verifiedCount}<span className="text-lg text-ink-muted">/{documents.length}</span></div>
                <p className="text-[12px] text-ink-muted">verified</p>
              </div>
            )}
          </div>

          {/* Error state */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" role="alert">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-[13px] font-semibold text-red-800">{error}</p>
                <button onClick={() => setError(null)} className="text-[12px] text-red-600 underline mt-1">Dismiss</button>
              </div>
            </div>
          )}

          {/* No business state */}
          {!businessId && !loading && (
            <div className="bg-white rounded-2xl border border-border p-10 text-center">
              <div className="w-14 h-14 bg-surface-3 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-ink mb-2">No business found</h2>
              <p className="text-[13px] text-ink-muted mb-6">Create your Business Passport first to manage documents.</p>
              <a href="/onboarding" className="inline-flex items-center gap-2 bg-ai text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-ai-dark transition-all text-[13px]">
                Create Business Passport →
              </a>
            </div>
          )}

          {/* Empty documents state */}
          {businessId && documents.length === 0 && (
            <div className="bg-white rounded-2xl border border-border p-10 text-center">
              <div className="w-14 h-14 bg-ai-light rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-ai" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-ink mb-2">No documents yet</h2>
              <p className="text-[13px] text-ink-muted mb-2">Upload your first document to get started.</p>
              <p className="text-[12px] text-ink-subtle">Supported: PDF, JPG, PNG</p>
            </div>
          )}

          {/* Documents list */}
          {businessId && documents.length > 0 && (
            <>
              {/* Progress */}
              <div className="bg-white rounded-xl border border-border p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[13px] font-semibold text-ink">Document verification progress</p>
                  <p className="text-[13px] font-bold text-ai">{Math.round((verifiedCount / documents.length) * 100)}%</p>
                </div>
                <div className="w-full bg-surface-3 rounded-full h-2" role="progressbar" aria-valuenow={Math.round((verifiedCount / documents.length) * 100)} aria-valuemin={0} aria-valuemax={100}>
                  <div className="bg-ai h-2 rounded-full transition-all" style={{ width: `${(verifiedCount / documents.length) * 100}%` }} />
                </div>
                <div className="flex gap-4 mt-3">
                  <span className="text-[12px] text-green-700 font-medium">✓ {verifiedCount} verified</span>
                  <span className="text-[12px] text-slate-600">○ {pendingCount} pending</span>
                </div>
              </div>

              {/* AI Info */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-indigo-600 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                    <span className="text-white text-[10px] font-bold">AI</span>
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-indigo-800 mb-1">AI Document Processing</p>
                    <p className="text-[12px] text-indigo-700">Every uploaded document is automatically classified, OCR-processed, and matched against your Business Passport. Mismatches are flagged for review.</p>
                  </div>
                </div>
              </div>

              {/* Documents Grid */}
              <div className="space-y-3" role="list" aria-label="Documents">
                {documents.map(doc => {
                  const config = statusConfig[doc.status] || statusConfig.pending;
                  const typeLabel = documentTypeLabels[doc.document_type] || doc.document_type;
                  const isUploading = uploading === doc.id;

                  return (
                    <div
                      key={doc.id}
                      role="listitem"
                      className={`bg-white rounded-xl border p-4 sm:p-5 transition-all ${
                        doc.status === 'mismatch' ? 'border-amber-300' :
                        doc.status === 'rejected' ? 'border-red-200' : 'border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            doc.status === 'verified' ? 'bg-green-50' :
                            doc.status === 'processing' ? 'bg-indigo-50' : 'bg-surface-3'
                          }`} aria-hidden="true">
                            <span className="text-lg">
                              {doc.status === 'verified' ? '✓' :
                               doc.status === 'processing' ? '⟳' :
                               doc.status === 'mismatch' ? '⚠' : '○'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-ink text-[14px] truncate">{typeLabel}</p>
                            <p className="text-[12px] text-ink-muted truncate">{doc.file_name}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                          {doc.passport_match_score != null && (
                            <div className="text-right hidden sm:block">
                              <p className="text-[11px] text-ink-muted">Match</p>
                              <p className="text-[13px] font-bold text-green-700">{doc.passport_match_score}%</p>
                            </div>
                          )}
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${config.bg} ${config.color}`}>
                            {config.label}
                          </span>
                          {doc.status === 'pending' && (
                            <button
                              onClick={() => handleUploadClick(doc.id)}
                              disabled={!!uploading}
                              aria-label={`Upload ${typeLabel}`}
                              className="bg-ai text-white text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:bg-ai-dark transition-all disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-1 whitespace-nowrap"
                            >
                              {isUploading ? 'Uploading…' : 'Upload'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Mismatch Alert */}
                      {doc.status === 'mismatch' && doc.mismatch_fields && (
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl" role="alert">
                          <div className="flex items-start gap-2">
                            <span className="text-amber-600 text-lg" aria-hidden="true">⚠️</span>
                            <div className="flex-1">
                              <p className="font-semibold text-ink text-[13px] mb-1">Information mismatch detected</p>
                              <p className="text-[12px] text-amber-800">
                                The following fields don&apos;t match your Business Passport:{' '}
                                {Object.keys(doc.mismatch_fields).join(', ')}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Processing state */}
                      {doc.status === 'processing' && (
                        <div className="mt-3 flex items-center gap-2 text-[12px] text-indigo-700">
                          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          AI is processing this document…
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Document Detail Modal */}
      {selectedDoc && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Document details"
          onClick={() => setSelectedDoc(null)}
        >
          <div
            className="bg-white rounded-2xl border border-border p-6 max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-ink text-[16px]">{documentTypeLabels[selectedDoc.document_type] || selectedDoc.document_type}</h2>
              <button
                onClick={() => setSelectedDoc(null)}
                aria-label="Close document details"
                className="p-1.5 rounded-lg hover:bg-surface-2 text-ink-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-[13px] text-ink-muted">{selectedDoc.file_name}</p>
            {selectedDoc.passport_match_score != null && (
              <p className="text-[13px] font-semibold text-green-700 mt-2">Match score: {selectedDoc.passport_match_score}%</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
